import { log } from '@livekit/components-core';
import { getAuthManager } from './auth/AuthManager';
import { telemetry } from './telemetry';
import { apiFetch, getUltraMeetClientConfig } from './ultra-meet-config';

/**
 * Enhanced fetch wrapper that automatically adds Authorization header with Bearer token
 * from authentication provider (OIDC or Email).
 * Includes automatic token refresh on 401 Unauthorized responses.
 *
 * @param url - The URL to fetch
 * @param options - Standard fetch options
 * @returns Promise with fetch Response
 *
 * @example
 * ```ts
 * const response = await authenticatedFetch('/api/v1/data', {
 *   method: 'POST',
 *   body: JSON.stringify({ key: 'value' })
 * });
 * ```
 */
export async function authenticatedFetch(url: string, options: RequestInit = {}): Promise<Response> {
    const authManager = getAuthManager();

    // Helper to execute fetch with current token
    const executeFetch = async (token: string | null): Promise<Response> => {
        const headers = new Headers(options.headers);
        if (token) {
            headers.set('Authorization', `Bearer ${token}`);
        }

        try {
            return await apiFetch(url, {
                ...options,
                headers
            });
        } catch (error) {
            log.error('Network request failed', { url, error });
            telemetry.capture('error.api_call', 'error', `Network request failed: ${url}`, {
                error_message: error instanceof Error ? error.message : String(error)
            });
            // Return a minimal Response-like object to avoid constructor RangeError
            return {
                ok: false,
                status: 0,
                statusText: 'Network error',
                headers: new Headers(),
                url,
                redirected: false,
                type: 'basic',
                body: null,
                bodyUsed: false,
                clone() {
                    return this;
                },
                async arrayBuffer() {
                    return new ArrayBuffer(0);
                },
                async blob() {
                    return new Blob();
                },
                async formData() {
                    return new FormData();
                },
                async json() {
                    throw new Error('Network error');
                },
                async text() {
                    return '';
                }
            } as unknown as Response;
        }
    };

    const configuredAccessTokenProvider = getUltraMeetClientConfig().accessTokenProvider;
    const configuredRefreshProvider = getUltraMeetClientConfig().refreshAccessTokenProvider;

    // Try initial request with current token
    let accessToken: string | null = null;
    try {
        accessToken = configuredAccessTokenProvider ? await configuredAccessTokenProvider() : await authManager.getAccessToken();
    } catch (error) {
        log.error('Failed to get access token:', error);
    }

    const response = await executeFetch(accessToken);

    // If 401 Unauthorized, try to refresh token and retry
    if (response.status === 401) {
        try {
            log.info('Token expired, attempting refresh...');
            let newToken: string | null = null;
            if (configuredRefreshProvider) {
                newToken = await configuredRefreshProvider();
            } else {
                await authManager.refreshTokens();
                // Get new token after refresh
                newToken = await authManager.getAccessToken();
            }

            // Retry the request with new token
            const retryResponse = await executeFetch(newToken);
            log.info('Request retried successfully with refreshed token');
            return retryResponse;
        } catch (refreshError) {
            log.error('Token refresh failed:', refreshError);
            telemetry.capture('error.api_call', 'warning', `Token refresh failed, signing out`, { url });
            // Token refresh failed - user needs to re-authenticate.
            // Sign out only when the built-in AuthManager owns the session.
            if (!configuredAccessTokenProvider) {
                try {
                    await authManager.signOut();
                } catch (signOutError) {
                    log.error('Failed to sign out after token refresh failure:', signOutError);
                }
            }
            // Return original 401 response
            return response;
        }
    }

    return response;
}

/**
 * Helper to make authenticated GET request and parse JSON response
 */
export async function authenticatedGet<T = any>(url: string): Promise<T> {
    const response = await authenticatedFetch(url);

    if (!response.ok) {
        throw new Error(`GET ${url} failed: ${response.status} ${response.statusText}`);
    }

    return response.json();
}

/**
 * Helper to make authenticated POST request with JSON body
 */
export async function authenticatedPost<T = any>(url: string, body?: any): Promise<T> {
    const response = await authenticatedFetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: body ? JSON.stringify(body) : undefined
    });

    if (!response.ok) {
        throw new Error(`POST ${url} failed: ${response.status} ${response.statusText}`);
    }

    return response.json();
}

/**
 * Helper to make authenticated PATCH request with JSON body
 */
export async function authenticatedPatch<T = any>(url: string, body?: any): Promise<T> {
    const response = await authenticatedFetch(url, {
        method: 'PATCH',
        headers: {
            'Content-Type': 'application/json'
        },
        body: body ? JSON.stringify(body) : undefined
    });

    if (!response.ok) {
        throw new Error(`PATCH ${url} failed: ${response.status} ${response.statusText}`);
    }

    return response.json();
}

/**
 * User info returned from /v1/user/me endpoint
 */
export interface UserInfo {
    id: string;
    email: string;
    first_name: string;
    last_name: string;
    photo_url?: string;
}

/**
 * Sign in request
 */
export interface SignInRequest {
    email: string;
    password: string;
}

/**
 * Sign in response from /v1/auth/sign-in endpoint
 */
export interface SignInResponse {
    result: string;
    token: string;
    refresh_token: string;
    user_uuid: string;
    email: string;
    first_name: string;
    last_name: string;
    access_token_issue_date: number;
    refresh_token_issue_date: number;
}

/**
 * Sign in user with email and password
 *
 * @param request - Sign in credentials
 * @returns Promise with SignInResponse containing tokens and user data
 * @throws Error if authentication fails
 *
 * @example
 * ```ts
 * const response = await signIn({ email: 'user@example.com', password: 'secret' });
 * console.log('Token:', response.token);
 * ```
 */
export async function signIn(request: SignInRequest): Promise<SignInResponse> {
    const response = await apiFetch('/api/v1/auth/sign-in', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(request)
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || `Sign in failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();

    if (data.result !== 'success') {
        throw new Error('Sign in failed');
    }

    return data;
}

/**
 * Get current authenticated user information from backend
 * This fetches the user's ID and profile from the server
 *
 * @returns Promise with UserInfo containing ID, email, and names
 * @throws Error if not authenticated or request fails
 *
 * @example
 * ```ts
 * const userInfo = await getCurrentUser();
 * console.log('User ID:', userInfo.id);
 * ```
 */
export async function getCurrentUser(): Promise<UserInfo> {
    const response = await authenticatedGet<{
        result: string;
        data: { uuid: string; email: string; first_name: string; last_name: string; photo_url?: string };
    }>('/api/v1/user/me');

    if (response.result !== 'success' || !response.data) {
        throw new Error('Failed to get current user info');
    }

    // Map uuid from backend to id for frontend
    return {
        id: response.data.uuid,
        email: response.data.email,
        first_name: response.data.first_name,
        last_name: response.data.last_name,
        photo_url: response.data.photo_url
    };
}

/**
 * Mute participant's microphone
 *
 * @param roomName - The name of the room
 * @param userUUID - UUID of the participant to mute
 * @returns Promise that resolves when operation is complete
 *
 * @example
 * ```ts
 * await muteMicrophone('room123', 'user-uuid-456');
 * ```
 */
export async function muteMicrophone(roomName: string, userUUID: string): Promise<void> {
    await authenticatedPatch('/api/v1/rooms/members/mute', {
        room_name: roomName,
        user_uuid: userUUID,
        track_type: 'audio'
    });
}

/**
 * Mute participant's camera
 *
 * @param roomName - The name of the room
 * @param userUUID - UUID of the participant to mute camera
 * @returns Promise that resolves when operation is complete
 *
 * @example
 * ```ts
 * await muteCamera('room123', 'user-uuid-456');
 * ```
 */
export async function muteCamera(roomName: string, userUUID: string): Promise<void> {
    await authenticatedPatch('/api/v1/rooms/members/mute', {
        room_name: roomName,
        user_uuid: userUUID,
        track_type: 'video'
    });
}

/**
 * Stop participant's screen sharing
 *
 * @param roomName - The name of the room
 * @param userUUID - UUID of the participant to stop screen sharing
 * @returns Promise that resolves when operation is complete
 *
 * @example
 * ```ts
 * await muteScreenShare('room123', 'user-uuid-456');
 * ```
 */
export async function muteScreenShare(roomName: string, userUUID: string): Promise<void> {
    await authenticatedPatch('/api/v1/rooms/members/mute', {
        room_name: roomName,
        user_uuid: userUUID,
        track_type: 'screen_share'
    });
}

/**
 * Kick participant from the room
 *
 * @param roomName - The name of the room
 * @param userUUID - UUID of the participant to kick
 * @returns Promise that resolves when operation is complete
 *
 * @example
 * ```ts
 * await kickParticipant('room123', 'user-uuid-456');
 * ```
 */
export async function kickParticipant(roomName: string, userUUID: string): Promise<void> {
    await authenticatedPost('/api/v1/rooms/members/kick', {
        room_name: roomName,
        user_uuid: userUUID
    });
}

/**
 * Mute all participants' microphones
 *
 * @param roomName - The name of the room
 * @returns Promise that resolves when operation is complete
 *
 * @example
 * ```ts
 * await muteAllMicrophones('room123');
 * ```
 */
export async function muteAllMicrophones(roomName: string): Promise<void> {
    await authenticatedPost('/api/v1/rooms/members/mute/all', {
        room_name: roomName,
        track_type: 'audio'
    });
}

/**
 * Mute all participants' cameras
 *
 * @param roomName - The name of the room
 * @returns Promise that resolves when operation is complete
 *
 * @example
 * ```ts
 * await muteAllCameras('room123');
 * ```
 */
export async function muteAllCameras(roomName: string): Promise<void> {
    await authenticatedPost('/api/v1/rooms/members/mute/all', {
        room_name: roomName,
        track_type: 'video'
    });
}

/**
 * Room creation response
 */
export interface CreateRoomResponse {
    result: string;
    data: {
        room_name: string;
    };
}

/**
 * Create anonymous room without authentication
 *
 * @param roomName - The name of the room to create (required)
 * @returns Promise with room creation response containing room_name
 * @throws Error if room creation fails
 *
 * @example
 * ```ts
 * const response = await createAnonymousRoom('my-meeting');
 * console.log('Room created:', response.data.room_name);
 * ```
 */
export async function createAnonymousRoom(roomName: string): Promise<CreateRoomResponse> {
    const response = await apiFetch('/api/v1/rooms/anonym', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            room_name: roomName,
            metadata: {
                can_publish_audio: true,
                can_publish_video: true,
                can_publish_screen: true,
                guest_access_allowed: true
            }
        })
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || `Failed to create room: ${response.status} ${response.statusText}`);
    }

    return response.json();
}
